import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { IoService } from '@core/services/io.service';
import { VersionService } from '@core/services/version.service';
import { LogSeverity, LoggingService } from '@core/services/logging.service';
import { DriveThruBffService } from '@shared/services/driveThruBff.service';
import { take } from 'rxjs/operators';

@Component({
  selector: 'app-lane-closed',
  templateUrl: './lane-closed.component.html',
  styleUrls: ['./lane-closed.component.scss'],
  standalone: false,
})
export class LaneClosedComponent implements OnInit, OnDestroy {
  private versionId = 0;
  private laneStatusInterval = 0;

  constructor(
    private router: Router,
    private logger: LoggingService,
    private ioService: IoService,
    private versionService: VersionService,
    private driveThruBffService: DriveThruBffService,
  ) {}

  async ngOnInit() {
    this.logger.writeLog('Lane is closed. Waiting for flag change.', {
      severity: LogSeverity.INFO,
    });

    this.ioService.onOpenLane((sessionId: string) => {
      this.onChangeLane(sessionId, 'Lane opened navigating to standby screen');
    });

    this.ioService.onStandbyLane((sessionId: string) => {
      this.onChangeLane(sessionId, 'Lane set to standby navigating to standby screen');
    });

    this.versionService.newVersion$.pipe(take(1)).subscribe((info) => {
      this.logger.writeLog('[LaneClosedComponent] Versions do not match, refreshing...', {
        ...info,
        severity: LogSeverity.INFO,
      });
      this.versionService.reloadNow();
    });

    const checkLaneStatus = async () => {
      this.logger.writeLog('[LaneClosedComponent#checkLaneStatus] Checking lane status', {
        severity: LogSeverity.INFO,
      });
      try {
        const result = await this.driveThruBffService.checkStoreStatus();
        this.logger.writeLog(`[LaneClosedComponent#checkLaneStatus] Lane status is ${result}`, {
          severity: LogSeverity.INFO,
        });
        if (result && result != 'CLOSED') {
          this.logger.writeLog(
            `[LaneClosedComponent#checkLaneStatus] Lane is ${result} navigating to start-conversation`,
            {
              severity: LogSeverity.INFO,
            },
          );
          this.navigateToStartConversation();
        }
      } catch (error) {
        this.logger.writeLog(`[LaneClosedComponent#checkLaneStatus] Error retrieving lane status`, {
          severity: LogSeverity.WARNING,
          error: error,
        });
      }
    };
    // Four minutes plus a random spread of 30 seconds. This is to: 1) not overlap with the version check in case the
    // page refreshes due to a new version.
    // 2) So that not all clients send the request at the exact same time to the backend.
    const laneStatusSeconds = 240 + Math.floor(Math.random() * 30);
    this.logger.writeLog(
      `[LaneClosedComponent] Scheduling checking of lane status every ${laneStatusSeconds} seconds`,
      {
        severity: LogSeverity.INFO,
      },
    );
    this.laneStatusInterval = window.setInterval(() => {
      checkLaneStatus();
    }, laneStatusSeconds * 1000);
  }

  ngOnDestroy() {
    clearTimeout(this.versionId);
    clearInterval(this.laneStatusInterval);
  }

  private navigateToStartConversation() {
    this.router
      .navigate(['/start-conversation'])
      .then((succeeded) => {
        if (!succeeded) {
          this.logger.writeLog('[LaneClosedComponent] Failed to navigate from lane closed to standby', {
            severity: LogSeverity.INFO,
          });
        }
      })
      .catch((error) => {
        this.logger.writeLog(
          `[LaneClosedComponent] Error navigating from lane closed to standby with error: ${error}`,
          {
            severity: LogSeverity.ERROR,
          },
        );
      });
  }

  private onChangeLane(sessionId: string, message: string) {
    this.logger.setSessionId(sessionId);
    this.logger.writeLog(message, { severity: LogSeverity.INFO });
    this.navigateToStartConversation();
  }
}
